import axios from 'axios';

const SYSTEM_PROMPT = `Eres un experto diseñador de presentaciones profesionales. 
Tu tarea es generar el contenido de una presentación en formato JSON basado en el prompt del usuario.

REGLAS CRÍTICAS:
1. Responde ÚNICAMENTE con un objeto JSON válido. No incluyas explicaciones ni texto fuera del JSON.
2. El JSON debe ser un array de objetos "slides".
3. Cada slide debe tener un "id" único y un "type".
4. Tipos de slides soportados y sus estructuras:
   - "title": { "id", "type": "title", "title", "subtitle", "date" }
   - "intro": { "id", "type": "intro", "title", "content", "bulletPoints": ["punto1", "punto2"], "imageUrl" (opcional) }
   - "image": { "id", "type": "image", "title", "imageUrl", "caption" }
   - "features": { "id", "type": "features", "title", "items": [{ "title", "description" }] }
   - "timeline": { "id", "type": "timeline", "title", "phases": [{ "phase", "title", "duration" }] }
   - "pricing": { "id", "type": "pricing", "title", "packages": [{ "name", "price", "features": [], "highlighted": true/false }] }
   - "contact": { "id", "type": "contact", "title", "content", "contactInfo": { "email", "phone", "website" } }

5. GENERACIÓN DE IMÁGENES:
   - Para las slides que necesiten una imagen (tipo "image" o "intro" con imageUrl), genera un "imagePrompt" en INGLÉS descriptivo y artístico.
   - El sistema convertirá ese "imagePrompt" en una URL real.
   - Intenta incluir al menos 2-3 slides con imágenes para hacer la presentación visualmente atractiva.

6. Ajusta el contenido al número de slides solicitado.
7. Usa un tono profesional y persuasivo adecuado al tema.
8. El idioma del contenido debe ser el solicitado (por defecto Español).`;

export const generatePresentation = async (prompt, options = {}) => {
    const {
        apiProvider = 'emergent',
        apiKey = '',
        slidesCount = 6,
        template = 'business',
        language = 'es'
    } = options;

    if (!apiKey) {
        throw new Error('API Key no configurada. Por favor, ve a Configuración.');
    }

    const userPrompt = `Genera una presentación de ${slidesCount} slides sobre: "${prompt}". 
  Plantilla sugerida: ${template}. 
  Idioma: ${language}.
  Asegúrate de que el contenido sea detallado y relevante para el prompt proporcionado.`;

    try {
        let response;

        if (apiProvider === 'claude') {
            // Claude API (Anthropic)
            // Note: Direct browser calls to Anthropic usually fail due to CORS. 
            // This is implemented assuming a proxy or CORS-enabled environment.
            response = await axios.post('https://api.anthropic.com/v1/messages', {
                model: 'claude-3-5-sonnet-20240620',
                max_tokens: 4000,
                system: SYSTEM_PROMPT,
                messages: [
                    { role: 'user', content: userPrompt }
                ]
            }, {
                headers: {
                    'x-api-key': apiKey,
                    'anthropic-version': '2023-06-01',
                    'Content-Type': 'application/json'
                }
            });

            const content = response.data.content[0].text;
            return parseAIResponse(content);
        } else if (apiProvider === 'emergent') {
            // Emergent LLM is OpenAI-compatible
            // According to user, this requires a token exchange
            // For now, we'll try the direct approach but with a warning or attempt token exchange if we had the endpoint
            response = await axios.post('https://api.emergent.io/v1/chat/completions', {
                model: 'gpt-4o',
                messages: [
                    { role: 'system', content: SYSTEM_PROMPT },
                    { role: 'user', content: userPrompt }
                ],
                temperature: 0.7,
                response_format: { type: 'json_object' }
            }, {
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json'
                }
            });

            const content = response.data.choices[0].message.content;
            return parseAIResponse(content);
        }
    } catch (error) {
        console.error('Error generating presentation:', error);
        throw new Error(error.response?.data?.error?.message || error.message || 'Error al conectar con la API de IA');
    }
};

const parseAIResponse = (content) => {
    try {
        // Attempt to find JSON if there's surrounding text
        const jsonMatch = content.match(/\[\s*\{.*\}\s*\]/s) || content.match(/\{\s*"slides".*\}/s);
        const jsonStr = jsonMatch ? jsonMatch[0] : content;

        let parsed = JSON.parse(jsonStr);

        // If it's wrapped in an object like { slides: [...] }
        if (parsed.slides && Array.isArray(parsed.slides)) {
            parsed = parsed.slides;
        }

        // Ensure it's an array
        if (!Array.isArray(parsed)) {
            // Check if it's already an array but not wrapped
            if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                // Sometimes AI returns { ...slides }
                const values = Object.values(parsed);
                if (values.length > 0 && Array.isArray(values[0])) {
                    parsed = values[0];
                }
            }
        }

        if (!Array.isArray(parsed)) {
            throw new Error('Formato de respuesta JSON no reconocido');
        }

        // Post-process to handle image generation
        return parsed.map(slide => {
            if (slide.imagePrompt) {
                const encodedPrompt = encodeURIComponent(slide.imagePrompt);
                slide.imageUrl = `https://pollinations.ai/p/${encodedPrompt}?width=1024&height=768&nologo=true`;
            }
            return slide;
        });
    } catch (e) {
        console.error('Failed to parse AI response:', content);
        throw new Error('La IA no devolvió un formato válido. Inténtalo de nuevo.');
    }
};
